import { Component, Input, SimpleChanges, OnChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { Reminder } from 'app/models/dto/reminder.model';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import { Subject } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';
import { Borrower } from 'app/models/dto/borrower.model';
import { NewReminderForm } from 'app/models/dto/new-reminder.model';
import { Store } from '@ngxs/store';
import { InitiateCall } from 'app/store/call/call.actions';
import { Alert } from 'app/models/dto/alert.model';
import { Contact } from 'app/models/dto/contact.model';
import { Router } from '@angular/router';
import { isBefore, isSameDay, toDate } from 'date-fns';

const colors: any = {
  green: {
    primary: '#71D18F',
    secondary: '#C1E6CC',
  },
  blue: {
    primary: '#02C0EE',
    secondary: '#D1E8FF',
  },
  yellow: {
    primary: '#F7CC45',
    secondary: '#FDF1BA',
  },
};

@Component({
  selector: 'app-schedule-reminder-full',
  templateUrl: './schedule-reminder-full.component.html',
  styleUrls: ['./schedule-reminder-full.component.scss'],
  standalone: false
})
export class ScheduleReminderFullComponent implements OnChanges {

  @Input() reminders: Reminder[];
  @Input() allRemindersLoaded = false;
  @Input() alerts: Alert[];
  @Output() create = new EventEmitter<NewReminderForm>();
  @Output() reschedule = new EventEmitter<{ reminderId: number, updates: any }>();
  @Output() complete = new EventEmitter<number>();
  @Output() cancel = new EventEmitter<number>();
  @Output() dismiss = new EventEmitter<Alert>();
  @Output() alert = new EventEmitter<Alert>();
  @ViewChild('borrowerSearchTrigger') borrowerSearchTrigger: MatMenuTrigger;
  @ViewChild('newEventTrigger') newEventTrigger: MatMenuTrigger;
  @ViewChild('selectedEventTrigger') selectedEventTrigger: MatMenuTrigger;
  @ViewChild('rescheduleEventTrigger') rescheduleEventTrigger: MatMenuTrigger;

  view: CalendarView = CalendarView.Week;
  CalendarView = CalendarView;
  viewDate: Date = new Date();
  activeDayIsOpen = false;
  refresh: Subject<void> = new Subject();
  events: CalendarEvent[] = [];
  eventDate: string | null;
  borrower: Borrower | null;
  selectedReminder: Reminder | null;
  selectedEventInFuture: boolean;
  menuX = 0;
  menuY = 0;

  constructor(
    private store: Store,
    private router: Router
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reminders) {
      this.events = [];
      this.reminders.forEach(reminder => {
        if (reminder.borrower) {
          this.events.push(this.formatAsCalendarEvent(reminder));
        }
      });
    }
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  dayClicked({ date, events }: { date: Date, events: CalendarEvent[] }): void {
    if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
      events.length === 0
    ) {
      this.activeDayIsOpen = false;
    } else {
      this.activeDayIsOpen = true;
    }
    this.viewDate = date;
  }

  eventTimesChanged({ event, newStart }): void {
    if (!isBefore(new Date(newStart), new Date())) {
      event.start = newStart;
      this.refresh.next();
      this.reschedule.emit({ reminderId: event.reminder.id, updates: { expires: this.formatDateToTimestamp(newStart) } });
    } else {
      this.alert.emit({
        message: 'You cannot schedule a reminder in the past.',
        level: 'error',
        dismissDelay: 5000
      });
    }
  }

  openBorrowerSearch(event) {
    let date;
    if(!(event.date instanceof Date)){
      date = new Date(event.date);
    } else{
      date = event.date;
    }
    if (!isBefore(date, new Date())) {
      this.eventDate = event.date;
      this.menuX = event.sourceEvent.x - 100;
      this.menuY = event.sourceEvent.y - 100;
      this.borrowerSearchTrigger.openMenu();
    }
  }

  openCreateEvent(borrower: Borrower) {
    this.borrower = borrower;
    this.newEventTrigger.openMenu();
  }

  createNewEvent(newReminder: NewReminderForm) {
    if (this.borrower) {
      if (!isBefore(toDate(newReminder.expires * 1000), new Date())) {
        const reminder = newReminder;
        reminder.borrowerId = this.borrower.id;
        this.create.emit(reminder);
        this.newEventTrigger.closeMenu();
        this.selectedReminder = null;
      } else {
        this.alert.emit({
          message: 'You cannot schedule a reminder in the past.',
          level: 'error',
          dismissDelay: 5000
        });
      }
    }
  }

  closeReminderForm() {
    this.selectedReminder = null;
    this.newEventTrigger.closeMenu();
    this.rescheduleEventTrigger.closeMenu();
  }

  dismissAlert(alert: Alert) {
    this.dismiss.emit(alert);
  }

  eventClicked(event) {
    this.selectedReminder = event.event.reminder;
    this.menuX = event.sourceEvent.x - 100;
    this.menuY = event.sourceEvent.y - 100;
    if (this.selectedReminder && !isBefore(new Date(this.selectedReminder.expires), new Date())) {
      this.selectedEventInFuture = true;
    } else {
      this.selectedEventInFuture = false;
    }
    this.selectedEventTrigger.openMenu();
  }

  reminderTime(date: string) {
    const jsDate = new Date(date);
    const hours = jsDate.getHours() > 12 ? jsDate.getHours() - 12 : jsDate.getHours();
    const minutes = jsDate.getMinutes() < 10 ? '0'.concat(jsDate.getMinutes().toString()) : jsDate.getMinutes();
    const meridiem  = jsDate.getHours() >= 12 ? 'PM' : 'AM';
    return `${hours}:${minutes} ${meridiem}`;
  }

  openReschedule() {
    this.rescheduleEventTrigger.openMenu();
  }

  clearBorrowerAndEventDate() {
    this.borrower = null;
    this.eventDate = null;
  }

  clearSelected() {
    this.selectedReminder = null;
  }

  messageBorrower() {
    if (this.selectedReminder) {
      this.completeReminder();
      this.router.navigateByUrl(`/borrower/${this.selectedReminder.borrower.id}?pane=messages`);
      this.selectedReminder = null;
    }
  }

  viewBorrower() {
    if (this.selectedReminder) {
      this.completeReminder();
      this.router.navigate(['/borrower/', this.selectedReminder.borrower.id]);
      this.selectedReminder = null;
    }
  }

  callBorrower() {
    if (this.selectedReminder && this.selectedReminder.borrower && this.selectedReminder.borrower.primary_contact) {
      this.store.dispatch(new InitiateCall({
        borrowerId: this.selectedReminder.borrower.id,
        phone: this.getIntlPhoneNumber(this.selectedReminder.borrower.primary_contact),
        borrower: this.selectedReminder.borrower
      }));
    } else {
      this.viewBorrower();
    }
    this.completeReminder();
    this.selectedReminder = null;
  }

  completeReminder() {
    if (this.selectedReminder) {
      this.complete.emit(this.selectedReminder.id);
    }
  }

  cancelReminder() {
    if (this.selectedReminder) {
      this.cancel.emit(this.selectedReminder.id);
      this.selectedReminder = null;
    }
  }

  rescheduleReminder(updatedReminder: NewReminderForm) {
    if  (this.selectedReminder) {
      if (!isBefore(toDate(updatedReminder.expires * 1000), new Date())) {
        this.rescheduleEventTrigger.closeMenu();
        this.reschedule.emit({
          reminderId: this.selectedReminder.id,
          updates: updatedReminder
        });
      } else {
        this.alert.emit({
          message: 'You cannot schedule a reminder in the past.',
          level: 'error',
          dismissDelay: 5000
        });
      }
      this.selectedReminder = null;
    }
  }

  formatAsCalendarEvent(reminder: any) {
    return {
      reminder: reminder,
      title: reminder.borrower.name,
      color: !isBefore( new Date(reminder.expires), new Date()) && !reminder.occurred ? colors.blue
        : isBefore( new Date(reminder.expires), new Date()) && !reminder.occurred ? colors.yellow
        : colors.green,
      start: new Date(reminder.expiresTimestamp * 1000),
      draggable: !reminder.occurred ? true : false,
      actions: [
        {
          label: reminder.action === 'call' ? '<span class="fa fa-phone"></span>'
            : reminder.action === 'todo' ? '<span class="fa fa-th-list"></span>'
            : reminder.action === 'message' ? '<span class="fa fa-comment"></span>'
            : '',
          onClick: (): void => {},
        },
      ],
    };
  }

  formatDateToTimestamp(date: string) {
    const jsDate = new Date(date);
    return Math.round((jsDate).getTime() / 1000);
  }

  private getIntlPhoneNumber(primaryContact: Contact) {
    return `+${primaryContact.mobilePhoneCountryCode || '1'}${primaryContact.mobilePhone}`;
  }
}
